import { useEffect, useRef } from 'react';

const TEXT_TYPES = new Set(['text', 'email', 'password', 'tel', 'number', 'url', '']);

/** The field's analytics name: `data-field`, else its `name` (react-hook-form). Never its value. */
const fieldName = (el) => {
    if (!el || !el.tagName) return null;
    const tag = el.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return null;
    if (tag === 'INPUT' && !TEXT_TYPES.has(el.type)) return null;
    return el.getAttribute('data-field') || el.name || null;
};

const textValue = (el) => (typeof el.value === 'string' ? el.value.trim() : '');

// Once per page load and step, so going back and forth doesn't count a field twice.
const completedOnce = new Set();

/**
 * Where visitors drop off inside a signup screen (v1 form or a v2 step):
 * - `signup_field_completed` when a field is left with a value (length only, never the value).
 * - `signup_left` when the tab is hidden or closed: time on the screen, fields touched / filled,
 *   the last focused field and the fields showing an error. Sent with sendBeacon so it survives
 *   the page closing.
 * - `signup_resumed` when the visitor comes back (app switch, checking email…), with the time away.
 *   A visitor whose last event is `signup_left` abandoned on that screen.
 * @param {{ track: Function, stepName: string, stepIndex?: number, fields?: boolean, enabled?: boolean }} options
 *   `track(name, props, options)`; `fields: false` when the screen already tracks its fields (v1).
 */
export const useSignupFormTracking = ({ track, stepName, stepIndex = null, fields = true, enabled = true }) => {
    const trackRef = useRef(track);
    trackRef.current = track;

    useEffect(() => {
        if (!enabled || !stepName) return undefined;
        const send = (name, props, options) => trackRef.current(name, props, options);
        const base = { step_name: stepName, ...(stepIndex !== null ? { step_index: stepIndex } : {}) };
        const viewedAt = Date.now();
        const touched = new Set();
        const filled = new Set();
        let lastField = null;
        let hiddenAt = null;

        const onFocusIn = (event) => {
            const name = fieldName(event.target);
            if (!name) return;
            touched.add(name);
            lastField = name;
        };
        const onFocusOut = (event) => {
            const name = fieldName(event.target);
            if (!name || event.target.tagName === 'SELECT') return;
            const value = textValue(event.target);
            if (!value) return;
            filled.add(name);
            const key = `${stepName}:${name}`;
            if (!fields || completedOnce.has(key)) return;
            completedOnce.add(key);
            send('signup_field_completed', {
                ...base,
                field: name,
                value_length: event.target.type === 'password' ? undefined : value.length,
                ms_since_step_viewed: Date.now() - viewedAt,
            });
        };
        const fieldsWithError = () =>
            Array.from(document.querySelectorAll('[aria-invalid="true"]'))
                .map(fieldName)
                .filter(Boolean);
        const onHide = () => {
            if (hiddenAt) return;
            hiddenAt = Date.now();
            send(
                'signup_left',
                {
                    ...base,
                    time_on_step_ms: hiddenAt - viewedAt,
                    fields_touched: Array.from(touched),
                    fields_filled: Array.from(filled),
                    last_field: lastField,
                    fields_with_error: fieldsWithError(),
                },
                { transport: 'sendBeacon', send_immediately: true }
            );
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                onHide();
            } else if (hiddenAt) {
                send('signup_resumed', { ...base, away_ms: Date.now() - hiddenAt });
                hiddenAt = null;
            }
        };

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onHide);
        window.addEventListener('pageshow', onVisibility);
        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onHide);
            window.removeEventListener('pageshow', onVisibility);
        };
    }, [stepName, stepIndex, fields, enabled]);
};
